import React from 'react';
import { DescriptionProvider } from './DescriptionContext';
import DescriptionHeadline from './DescriptionHeadline';
import DescriptionMetadata from './DescriptionMetadata';
import DescriptionThumbnail from './DescriptionThumbnail';
import * as S from './styles';
import { DescriptionProps } from './types';

function ForwardedDescriptionComponent<As extends React.ElementType = 'div'>(
  { as, children, isDisabled = false, ...props }: DescriptionProps<As>,
  ref: React.ForwardedRef<React.ElementRef<As>>,
): React.ReactElement | null {
  const Component = as ?? 'div';

  return (
    <DescriptionProvider isDisabled={isDisabled}>
      <S.Description
        forwardedAs={Component}
        data-disabled={isDisabled}
        ref={ref}
        {...props}
      >
        {children}
      </S.Description>
    </DescriptionProvider>
  );
}

const DescriptionComponent = React.forwardRef(
  ForwardedDescriptionComponent,
) as <As extends React.ElementType = 'div'>(
  props: DescriptionProps<As> & { ref?: React.Ref<React.ElementRef<As>> },
) => React.ReactElement | null;

const Header = Object.assign(S.Header, {
  Actions: S.HeaderActions,
});

/**
 * Renders details about an item - typically media, a podcast, general list item,
 * project, etc.
 *
 * The description is broken up into 3 parts with a header, body, and footer and while
 * some of the general layout and styles are baked in, it's customizable to fit
 * various use cases.
 */
const Description = Object.assign(DescriptionComponent, {
  Header,
  Body: S.Body,
  Footer: S.Footer,
  Headline: DescriptionHeadline,
  Meta: DescriptionMetadata,
  Thumbnail: DescriptionThumbnail,
});

export default Description;
